import { useRouter } from 'expo-router';
import { Plus } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet } from 'react-native';
import { StatusBadge } from '../../../src/components/primitives/BadgePresets';
import { Box } from '../../../src/components/primitives/Box';
import { Button } from '../../../src/components/primitives/Button';
import { EmptyState } from '../../../src/components/primitives/EmptyState';
import { ListRow } from '../../../src/components/primitives/ListRow';
import { HStack, VStack } from '../../../src/components/primitives/Stack';
import { Text } from '../../../src/components/primitives/Text';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useTranslation } from '../../../src/contexts/TranslationContext';
import {
  canManageLeadSourceConnections,
  setPendingLeadSourceSecret,
} from '../../../src/features/lead-sources/utils';
import { useCurrentOrgRole } from '../../../src/hooks/useAuthOrgRole';
import {
  useCreateLeadSourceConnection,
  useLeadSourceCatalog,
  useLeadSourceConnections,
} from '../../../src/hooks/useLeadSources';
import type { LeadSourceConnectionStatus } from '../../../src/lib/leadSourcesApi';
import { colors } from '../../../src/theme/colors';
import { tokens } from '../../../src/theme/tokens';

const STATUS_TO_BADGE: Record<LeadSourceConnectionStatus, 'success' | 'warning' | 'danger'> = {
  active: 'success',
  paused: 'warning',
  archived: 'danger',
};

export default function LeadSourcesScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { currentOrganization } = useAuth();
  const role = useCurrentOrgRole();
  const canManage = canManageLeadSourceConnections(role);
  const organizationId = currentOrganization?.id;

  const { data: connections, isLoading: connectionsLoading } =
    useLeadSourceConnections(organizationId);
  const {
    data: catalog,
    isLoading: catalogLoading,
    isError: catalogError,
    refetch: refetchCatalog,
  } = useLeadSourceCatalog();
  const { mutateAsync: createConnection } = useCreateLeadSourceConnection();
  const [creatingProviderKey, setCreatingProviderKey] = useState<string | null>(null);

  const handleCreateFromCatalog = useCallback(
    async (providerKey: string, label: string) => {
      if (!canManage || creatingProviderKey) return;
      setCreatingProviderKey(providerKey);
      try {
        const result = await createConnection({
          providerKey,
          name: label,
          leadSource: providerKey,
          authMode: 'path_token',
        });
        setPendingLeadSourceSecret(result.connection.id, result.credential);
        router.push(`/lead-sources/${result.connection.id}` as never);
      } finally {
        setCreatingProviderKey(null);
      }
    },
    [canManage, creatingProviderKey, createConnection, router],
  );

  return (
    <ScrollView testID="lead_sources_screen" contentContainerStyle={styles.content}>
      <VStack spacing="xs">
        <Text variant="h4" color={colors.text.primary} accessibilityRole="header">
          {t('leadSources.page.title')}
        </Text>
        <Text variant="caption" color={colors.text.tertiary}>
          {t('leadSources.page.subtitle')}
        </Text>
      </VStack>

      {!canManage && (
        <Box padding="md" backgroundColor={colors.status.info.bg} borderRadius="md">
          <Text variant="caption" color={colors.text.secondary}>
            {t('leadSources.readOnlyNotice')}
          </Text>
        </Box>
      )}

      <VStack spacing="sm">
        <Text variant="h6" color={colors.text.primary}>
          {t('leadSources.sections.connected')}
        </Text>
        {connectionsLoading ? (
          <ActivityIndicator color={colors.primary.neon} />
        ) : connections && connections.length > 0 ? (
          connections.map((connection) => (
            <ListRow
              key={connection.id}
              as="pressable"
              title={connection.name}
              subtitle={connection.providerKey}
              onPress={() => router.push(`/lead-sources/${connection.id}` as never)}
              accessibilityLabel={`${connection.name}, ${t(`leadSources.status.${connection.status}`)}`}
              trailing={
                <StatusBadge status={STATUS_TO_BADGE[connection.status]}>
                  {t(`leadSources.status.${connection.status}`)}
                </StatusBadge>
              }
            />
          ))
        ) : (
          <EmptyState
            title={t('leadSources.empty.connectedTitle')}
            description={t('leadSources.empty.connectedDescription')}
          />
        )}
      </VStack>

      <VStack spacing="sm">
        <Text variant="h6" color={colors.text.primary}>
          {t('leadSources.sections.available')}
        </Text>
        {catalogLoading ? (
          <ActivityIndicator
            testID="lead_sources_catalog_loading"
            color={colors.primary.neon}
            accessibilityLabel={t('common.loading')}
          />
        ) : catalogError ? (
          <VStack
            spacing="sm"
            testID="lead_sources_catalog_error"
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
          >
            <EmptyState
              title={t('leadSources.errors.catalogLoadFailedTitle')}
              description={t('leadSources.errors.catalogLoadFailed')}
            />
            <Button
              variant="secondary"
              testID="lead_sources_catalog_retry"
              accessibilityLabel={t('leadSources.actions.retry')}
              onPress={() => refetchCatalog()}
            >
              {t('leadSources.actions.retry')}
            </Button>
          </VStack>
        ) : (
          (catalog ?? []).map((provider) => (
            <ListRow
              key={provider.providerKey}
              as="pressable"
              title={provider.label}
              subtitle={provider.description}
              disabled={!canManage || !!creatingProviderKey}
              onPress={() => handleCreateFromCatalog(provider.providerKey, provider.label)}
              accessibilityLabel={`${t('leadSources.actions.create')}: ${provider.label}`}
              trailing={
                creatingProviderKey === provider.providerKey ? (
                  <ActivityIndicator color={colors.primary.neon} />
                ) : (
                  <HStack style={styles.plusIcon}>
                    <Plus size={18} color={colors.text.tertiary} />
                  </HStack>
                )
              }
            />
          ))
        )}
      </VStack>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: tokens.spacing.lg,
    gap: tokens.spacing.xl,
  },
  plusIcon: {
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
